import { useState, useEffect, useRef, useCallback } from "react";
import { FaUserFriends, FaCalendar } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { BiSolidCategory } from "react-icons/bi";
import { IoShareSocialSharp } from "react-icons/io5";
import { CiGlobe } from "react-icons/ci";
import { getAvailableWikiData, getDomainInfo } from "../../../api/wiki";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import { TILE_CONFIG, SYMBOL_MAP } from "./constants";
import { useCompanyContext } from "../../../context";
import SplitBackground from "./SplitBackground";
import LegacyCrypto from "./LegacyCrypto";
import LegacyDomainsIP from "./LegacyDomainsIP";
import CVEGraphPanel from "./CVEGraphPanel";
import { useRightSidebar } from "../../contexts/RightSidebarContext";
import { getTheme } from "../../Universal/useNavTheme";
import ArticleDetailPanel from "../../Universal/ArticleCardDetailPanel";

const isDD = process.env.REACT_APP_IS_DD === "true";
const isCP = process.env.REACT_APP_IS_CP === "true";
const PREVIEW_COUNT = 5;

const rowClass =
  "py-3 border-b border-surfaceBorder last:border-0 cursor-pointer transition-colors hover:bg-velBlue/5 active:bg-velBlue/10 group";

const cleanString = (value) => {
  if (value == null) return "";

  const str = String(value).trim();

  if (
    !str ||
    str === "None" ||
    str === "null" ||
    str === "undefined" ||
    str === "NaN"
  ) {
    return "";
  }

  return str;
};

const formatCompanySize = (value) => {
  if (Array.isArray(value)) {
    const [min, max] = value;

    if (min && max) return `${min} – ${max} employees`;
    if (min) return `${min} employees`;

    return "";
  }

  return cleanString(value);
};

const formatHq = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return cleanString(value);
  }

  if (typeof value === "object") {
    const city = cleanString(value.city);
    const state = cleanString(value.state);
    const country = cleanString(value.country);

    return [city, state, country].filter(Boolean).join(", ");
  }

  return "";
};

const formatFollowers = (value) => {
  const str = cleanString(value);

  if (!str) return "";

  if (str.toLowerCase().includes("followers")) return str;

  const numeric = Number(String(str).replace(/[^\d]/g, ""));

  if (!Number.isFinite(numeric) || numeric <= 0) return str;

  return `${numeric.toLocaleString()} followers`;
};

const CourtCaseRow = ({ item, onOpen }) => {
  const s = (v) => (v ?? "").toString().trim();
  const title = s(item.title || item.case_name);
  const content = s(item.content);
  const dateFiled = s(item.date_filed);
  const docketNumber = s(item.docket_number);

  if (!title && !content) return null;

  return (
    <div className={rowClass} onClick={() => onOpen?.(item)}>
      {title && (
        <p className="text-sm font-medium text-mainText leading-snug">
          {title}
        </p>
      )}

      {content && (
        <p className="text-xs text-mutedText mt-1 leading-relaxed line-clamp-2">
          {content}
        </p>
      )}

      {(dateFiled || (docketNumber && docketNumber !== "Unknown")) && (
        <div className="flex items-center gap-3 mt-2">
          {dateFiled && (
            <span className="text-xs px-2 py-0.5 rounded bg-innerBg border border-surfaceBorder text-mutedText">
              {dateFiled}
            </span>
          )}

          {docketNumber && docketNumber !== "Unknown" && (
            <span className="text-xs font-mono text-mutedText/60">
              {docketNumber}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const EmployeeRow = ({ item, onOpen }) => {
  const jr = item?.json_response ?? {};

  if (jr.status === "running") return null;

  const name = (jr.name ?? "").trim();
  const headline = (jr.headline ?? "").trim();
  const location = (jr.location ?? "").trim();

  if (!name && !location) return null;

  return (
    <div className={rowClass} onClick={() => onOpen?.(item)}>
      <div className="flex-1 min-w-0">
        {name && (
          <p className="text-sm font-medium text-mainText truncate">{name}</p>
        )}

        {headline && (
          <p className="text-xs text-mutedText/60 mt-0.5 truncate">
            {headline}
          </p>
        )}

        {location && (
          <p className="text-xs text-mutedText mt-0.5 truncate">{location}</p>
        )}
      </div>
    </div>
  );
};

const NeighborRow = ({ item, onOpen }) => {
  const s = (v) => (v ?? "").toString().trim();
  const neighbor = s(item.neighbor);
  const url = s(item.url);
  const address = s(item.address);

  if (!neighbor && !url) return null;

  return (
    <div className={rowClass} onClick={() => onOpen?.(item)}>
      <div className="flex-1 min-w-0">
        {neighbor && (
          <p className="text-sm font-medium text-mainText truncate">
            {neighbor}
          </p>
        )}

        {address && (
          <p className="text-xs text-mutedText/60 mt-0.5 truncate">{address}</p>
        )}

        {url && (
          <p className="text-xs text-mutedText mt-0.5 truncate">
            {url.replace(/^https?:\/\//, "")}
          </p>
        )}
      </div>
    </div>
  );
};

const SanctionRow = ({ item, onOpen }) => {
  const title = (item.sanctions_title ?? "").toString().trim();
  const summary = (item.sanctions_summary ?? "").toString().trim();

  if (!title) return null;

  return (
    <div className={rowClass} onClick={() => onOpen?.(item)}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-mainText leading-snug">
            {title}
          </p>

          {summary && (
            <p className="text-xs text-mutedText mt-1 line-clamp-2 leading-relaxed">
              {summary}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CryptoRowInline = ({ item, onOpen }) => {
  const coin = (item.coin ?? "").toString().trim();
  const address = (item.address ?? "").toString().trim();
  const title = (item.title ?? "").toString().trim();

  if (!coin || coin.startsWith("http") || coin.startsWith("Http")) return null;

  const resolvedSymbol = SYMBOL_MAP[coin] || coin.toLowerCase() || "btc";

  return (
    <div
      className={`flex items-center gap-3 ${rowClass}`}
      onClick={() => onOpen?.(item)}
    >
      <div
        className="flex shrink-0 items-center justify-center rounded-xl bg-innerBg font-mono font-semibold text-mutedText"
        style={{
          width: 32,
          height: 32,
          border: "1px solid var(--color-surfaceBorder)",
          fontSize: 10,
          letterSpacing: "0.04em",
        }}
      >
        {resolvedSymbol.slice(0, 4).toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-mainText">{coin}</span>

          {title && (
            <span className="text-xs text-mutedText bg-innerBg rounded px-1.5 py-0.5 border border-surfaceBorder">
              {title}
            </span>
          )}
        </div>

        {address && (
          <p className="text-xs text-mutedText font-mono truncate mt-0.5">
            {address}
          </p>
        )}
      </div>
    </div>
  );
};

const DomainRow = ({ item, onOpen }) => {
  const s = (v) => (v ?? "").toString().trim();
  const url = s(item.url);
  const commonName = s(item.common_name);
  const issuerName = s(item.issuer_name);

  if (!url && !commonName) return null;

  const display = url || commonName;

  return (
    <div className={rowClass} onClick={() => onOpen?.(item)}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-mainText truncate">{display}</p>

        {issuerName && (
          <p className="text-xs text-mutedText mt-0.5 truncate">{issuerName}</p>
        )}
      </div>
    </div>
  );
};

const BOLsRow = ({ item, onOpen }) => {
  const carrierCode = (item.carrier_code ?? "").toString().trim();
  const vesselName = (item.vessel_name ?? "").toString().trim();
  const masterBol = (item.master_bol_number ?? "").toString().trim();
  const qty = item.manifest_quantity;
  const unit = (item.manifest_unit ?? "").toString().trim();
  const qtyLabel = qty != null ? `${qty} ${unit}`.trim() : "";
  const fromPort = (item.foreign_port_of_lading_name ?? "").toString().trim();
  const toPort = (item.port_of_unlading_name ?? "").toString().trim();
  const route =
    fromPort && toPort ? `${fromPort} → ${toPort}` : fromPort || toPort;

  if (!carrierCode && !vesselName) return null;

  return (
    <div
      className={`flex items-center gap-3 ${rowClass}`}
      onClick={() => onOpen?.(item)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-mainText">
            {vesselName || masterBol}
          </span>

          {qtyLabel && (
            <span className="text-xs text-mutedText bg-innerBg rounded px-1.5 py-0.5 border border-surfaceBorder">
              {qtyLabel}
            </span>
          )}
        </div>

        {route && (
          <p className="text-xs text-mutedText truncate mt-0.5">{route}</p>
        )}
      </div>
    </div>
  );
};

const IPsRow = ({ item, onOpen }) => {
  const s = (v) => (v ?? "").toString().trim();
  const ip = s(item.ip_address);
  const commonName = s(item.common_name);

  if (!ip && !commonName) return null;

  const getVal = (arr, type) => {
    if (!Array.isArray(arr)) return "";
    const found = arr.find((x) => x.type === type);
    return s(found?.data ?? "");
  };

  const asn = getVal(item.Info, "ASN");
  const country = getVal(item.Location, "Country");
  const city = getVal(item.Location, "City");
  const location = [city && city !== "false" ? city : null, country]
    .filter(Boolean)
    .join(", ");

  const blacklisted = Array.isArray(item.Blacklist)
    ? item.Blacklist.some(
        (b) => b.data && b.data !== "Not listed" && b.data !== "",
      )
    : false;

  const openServices = Array.isArray(item.Services)
    ? item.Services.filter((sv) => sv.data === "Open").map((sv) => sv.type)
    : [];

  return (
    <div className={rowClass} onClick={() => onOpen?.(item)}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-mainText font-mono">
            {ip}
          </span>

          {blacklisted && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400">
              Blacklisted
            </span>
          )}
        </div>

        {commonName && (
          <p className="text-xs text-mutedText mt-0.5 truncate">{commonName}</p>
        )}

        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {location && (
            <span className="text-xs text-mutedText/70">{location}</span>
          )}

          {asn && (
            <span className="text-xs text-mutedText/70 font-mono">{asn}</span>
          )}

          {openServices.map((svc) => (
            <span
              key={svc}
              className="text-xs px-1.5 py-0.5 rounded bg-innerBg border border-surfaceBorder text-mutedText"
            >
              {svc}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const MLDRow = ({ item, onOpen }) => {
  const s = (v) => (v ?? "").toString().trim();
  const ip = s(item.ip_address);
  const country = s(item.country);
  const carrier = s(item.carrier);
  const manufacturer = s(item.manufacturer);
  const deviceModel = s(item.device_model);
  const adIdType = s(item.advertising_id_type);
  const timestamp = item.bazze_timestamp
    ? s(item.bazze_timestamp).split(" ")[0]
    : "";
  const lat = item.latitude ? parseFloat(item.latitude).toFixed(3) : null;
  const lng = item.longitude ? parseFloat(item.longitude).toFixed(3) : null;
  const coords = lat && lng ? `${lat}, ${lng}` : null;

  if (!ip && !carrier && !country) return null;

  return (
    <div className={rowClass} onClick={() => onOpen?.(item)}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {ip && (
            <span className="text-sm font-medium text-mainText font-mono">
              {ip}
            </span>
          )}

          {country && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-innerBg border border-surfaceBorder text-mutedText">
              {country}
            </span>
          )}

          {adIdType && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-innerBg border border-surfaceBorder text-mutedText uppercase">
              {adIdType}
            </span>
          )}
        </div>

        {(carrier || deviceModel || manufacturer) && (
          <p className="text-xs text-mutedText mt-0.5 truncate">
            {[carrier, manufacturer, deviceModel].filter(Boolean).join(" · ")}
          </p>
        )}

        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {coords && (
            <span className="text-xs text-mutedText/70 font-mono">
              {coords}
            </span>
          )}

          {timestamp && (
            <span className="text-xs text-mutedText/60">{timestamp}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const INLINE_RENDERER = {
  "Court Cases": {
    Row: CourtCaseRow,
    canRender: (d) => !!(d.case_name || d.content || d.title),
  },
  "Key Employees": {
    Row: EmployeeRow,
    canRender: () => true,
  },
  Neighbors: {
    Row: NeighborRow,
    canRender: (d) => !!(d.neighbor || d.url),
  },
  Sanctions: {
    Row: SanctionRow,
    canRender: (d) => !!d.sanctions_title,
  },
  Crypto: {
    Row: CryptoRowInline,
    canRender: (d) => {
      const c = (d.coin ?? "").trim();
      return !!(c && !c.startsWith("http"));
    },
  },
  Domains: {
    Row: DomainRow,
    canRender: (d) => !!(d.url || d.common_name),
  },
  IPs: {
    Row: IPsRow,
    canRender: (d) => !!(d.ip_address || d.common_name),
  },
  BOLs: {
    Row: BOLsRow,
    canRender: () => true,
  },
  MLD: {
    Row: MLDRow,
    canRender: () => true,
  },
};

const SkeletonBlock = ({ animDelay }) => (
  <div
    className="rounded-lg border border-surfaceBorder bg-surface/80"
    style={{ padding: "14px 18px", minHeight: 180, animDelay }}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="h-2.5 w-20 rounded bg-innerBg animate-pulse" />
      <div className="h-2.5 w-12 rounded bg-innerBg animate-pulse" />
    </div>

    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="py-3 border-b border-surfaceBorder last:border-0 flex items-center gap-3"
      >
        <div className="h-8 w-8 rounded-full bg-innerBg animate-pulse shrink-0" />
        <div className="flex-1 flex flex-col gap-1.5">
          <div className="h-2.5 rounded bg-innerBg animate-pulse w-3/4" />
          <div className="h-2 rounded bg-innerBg animate-pulse w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

const SectionBlock = ({
  sectionKey,
  items,
  onViewAll,
  onOpen,
  visible,
  animDelay,
}) => {
  const renderer = INLINE_RENDERER[sectionKey];

  if (!renderer) return null;
  if (items === null) return <SkeletonBlock animDelay={animDelay} />;

  const safeItems = Array.isArray(items) ? items : [];
  const valid = safeItems.filter((d) => renderer.canRender(d));

  if (!valid.length) return null;

  const preview = valid.slice(0, PREVIEW_COUNT);
  const tileConfig = TILE_CONFIG[sectionKey];

  return (
    <div
      className="rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md shadow-login-panel overflow-hidden"
      style={{
        transition: `all 0.5s ease-out ${animDelay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
      }}
    >
      <SplitBackground
        imageUrl={tileConfig?.image}
        color={tileConfig?.color}
        title={tileConfig?.title ?? sectionKey}
        setCurrentSubSection={() => onViewAll(sectionKey)}
      />

      <div className="flex flex-col" style={{ padding: "0 18px 0" }}>
        <div>
          {preview.map((item, i) => (
            <renderer.Row
              key={item.document_id || `${sectionKey}-${i}`}
              item={item}
              onOpen={onOpen}
            />
          ))}
        </div>

        <button
          onClick={() => onViewAll(sectionKey)}
          className="text-xs text-velBlue hover:underline py-3 w-full text-center transition-colors focus-visible:outline-none border-t border-surfaceBorder mt-1"
        >
          View all ({valid.length})
        </button>
      </div>
    </div>
  );
};

const DescriptionPanel = ({ text, animStyle }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const innerRef = useRef(null);
  const MAX_H = 120;

  useEffect(() => {
    if (innerRef.current) {
      setOverflows(innerRef.current.scrollHeight > MAX_H + 4);
    }
  }, [text]);

  return (
    <div
      className="rounded-lg border border-surfaceBorder bg-surface/80 p-6 shadow-login-panel backdrop-blur-md flex flex-col"
      style={{ ...animStyle, flex: 2 }}
    >
      <div
        style={{
          overflow: "hidden",
          maxHeight: expanded ? "none" : MAX_H,
          transition: "max-height 0.3s ease",
        }}
      >
        {cleanString(text) ? (
          <p
            ref={innerRef}
            className="text-sm leading-relaxed text-mainText"
            style={{ margin: 0 }}
          >
            {text}
          </p>
        ) : (
          <p className="text-xs text-mutedText" style={{ margin: 0 }}>
            No company description available
          </p>
        )}
      </div>

      {overflows && (
        <button
          onClick={() => setExpanded((p) => !p)}
          className="mt-3 text-xs text-velBlue hover:underline self-start focus-visible:outline-none"
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
};

const Overview = (props) => {
  const [overviewData, setOverviewData] = useState(null);
  const [sectionData, setSectionData] = useState({});
  const [knownSections, setKnownSections] = useState([]);
  const [topVisible, setTopVisible] = useState(false);
  const [sectionsVisible, setSectionsVisible] = useState(false);
  const [companyDomainInfo, setCompanyDomainInfo] = useState([]);

  const { companyKey, currentCompany } = useCompanyContext();
  const { setRightComponent, setIsRightOpen, setRightSidebarMode } =
    useRightSidebar();

  const pad = Math.max(props.baseMargin, 24);
  const currentCase = isDD ? currentCompany?.target : props.currentCase;

  const openCrypto = useCallback(
    (item) => {
      if (!item?.document_id) return;

      setRightSidebarMode(null);
      setIsRightOpen(true);
      setRightComponent(
        <ArticleDetailPanel
          article={item}
          cardId={item.document_id}
          t={getTheme(document.documentElement.classList.contains("dark"))}
        />,
      );
    },
    [setRightComponent, setIsRightOpen, setRightSidebarMode],
  );

  const openArticle = useCallback(
    (item) => {
      if (!item?.document_id) return;

      setRightSidebarMode(null);
      setIsRightOpen(true);
      setRightComponent(
        <ArticleDetailPanel
          article={item}
          cardId={item.document_id}
          t={getTheme(document.documentElement.classList.contains("dark"))}
        />,
      );
    },
    [setRightComponent, setIsRightOpen, setRightSidebarMode],
  );

  useEffect(() => {
    if (!currentCase) return;

    getDomainInfo(currentCase)
      .then((res) => setCompanyDomainInfo(res?.data || []))
      .catch((e) => console.log(e));
  }, [currentCase]);

  useEffect(() => {
    if (!currentCase) return;

    setOverviewData(null);
    setSectionData({});
    setKnownSections([]);
    setTopVisible(false);
    setSectionsVisible(false);

    getAvailableWikiData(currentCase)
      .then((response) => {
        const items = {};

        if (Array.isArray(response)) {
          response.forEach((item) => {
            items[item.sub_section] = item.data;
          });
        }

        setOverviewData(items);

        requestAnimationFrame(() =>
          requestAnimationFrame(() => setTopVisible(true)),
        );

        const available = Object.keys(INLINE_RENDERER);
        setKnownSections(available);

        const initMap = {};

        available.forEach((k) => {
          initMap[k] = null;
        });

        setSectionData(initMap);

        available.forEach((key) => {
          const config = TILE_CONFIG[key];

          if (!config?.fetcher) {
            setSectionData((prev) => ({ ...prev, [key]: [] }));
            return;
          }

          config
            .fetcher(currentCase)
            .then((res) => {
              const data = res?.data ?? (Array.isArray(res) ? res : []);
              setSectionData((prev) => ({
                ...prev,
                [key]: Array.isArray(data) ? data : [],
              }));
              setSectionsVisible(true);
            })
            .catch(() => {
              setSectionData((prev) => ({ ...prev, [key]: [] }));
            });
        });
      })
      .catch(() => {
        setOverviewData({});
      });
  }, [currentCase, companyKey]);

  if (!overviewData) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: "100%", marginTop: 24 }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  const overview = overviewData.Overview || {};

  const infoItems = [
    {
      icon: FaCalendar,
      label: cleanString(overview.founded_year)
        ? `Founded in ${cleanString(overview.founded_year)}`
        : null,
    },
    {
      icon: FaUserFriends,
      label:
        formatCompanySize(overview.company_size) ||
        formatCompanySize(overview.company_size_on_linkedin) ||
        null,
    },
    {
      icon: FaLocationDot,
      label: formatHq(overview.hq) || null,
    },
    {
      icon: BiSolidCategory,
      label: cleanString(overview.industry) || null,
    },
    {
      icon: IoShareSocialSharp,
      label: formatFollowers(overview.follower_count) || null,
    },
    {
      icon: CiGlobe,
      label: cleanString(overview.website) || null,
    },
  ].filter((i) => i.label);

  const animStyle = (delay = 0) => ({
    transition: `all 0.5s ease-out ${delay}ms`,
    opacity: topVisible ? 1 : 0,
    transform: topVisible ? "translateY(0)" : "translateY(16px)",
  });

  const isDark = document.documentElement.classList.contains("dark");
  const descriptionText =
    cleanString(overview.description) || cleanString(overview.specialities);

  return (
    <div
      className="font-poppins font-normal"
      style={{
        marginTop: 24,
        paddingBottom: 32,
        paddingLeft: pad,
        paddingRight: pad,
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex gap-5 items-stretch">
          <DescriptionPanel text={descriptionText} animStyle={animStyle(0)} />

          <div
            className="rounded-lg border border-surfaceBorder bg-surface/80 p-6 shadow-login-panel backdrop-blur-md"
            style={{ ...animStyle(75), flex: 1 }}
          >
            <div className="flex flex-col gap-4">
              {infoItems.length > 0 ? (
                infoItems.map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-innerBg">
                      <Icon
                        aria-hidden
                        style={{ width: 14, height: 14 }}
                        className="text-velBlue"
                      />
                    </div>

                    <span className="text-sm text-mainText">{label}</span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-mutedText" style={{ margin: 0 }}>
                  No company details available
                </p>
              )}
            </div>
          </div>
        </div>

        {knownSections.length > 0 && (
          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            }}
          >
            {knownSections
              .filter(
                (key) =>
                  sectionData[key] === null ||
                  (sectionData[key]?.length ?? 0) > 0,
              )
              .map((key, index) => (
                <SectionBlock
                  key={key}
                  sectionKey={key}
                  items={sectionData[key] ?? null}
                  onViewAll={props.setCurrentSubSection}
                  onOpen={openArticle}
                  visible={sectionsVisible}
                  animDelay={index * 40}
                />
              ))}
          </div>
        )}

        {isCP && (
          <div
            className="rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md overflow-hidden"
            style={{
              ...animStyle(120),
              height: 640,
            }}
          >
            <CVEGraphPanel
              data={props.cveGraphData}
              dark={isDark}
              size="roomy"
            />
          </div>
        )}

        <LegacyCrypto
          companyCryptoData={overviewData.Crypto}
          onOpen={openCrypto}
        />

        <LegacyDomainsIP companyDomainInfo={companyDomainInfo} />
      </div>
    </div>
  );
};

export default Overview;
